import { useQuery } from "@tanstack/react-query";
import React, { useState, useMemo, useEffect, useRef } from 'react';
import journeyTracker from '../utils/journeyTracker';
import { FiChevronRight, FiChevronDown, FiChevronUp, FiTrendingUp, FiAlertTriangle, FiActivity, FiTrendingDown, FiTarget, FiBarChart2, FiDollarSign, FiFileText, FiGrid, FiCompass } from 'react-icons/fi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagicWandSparkles } from '@fortawesome/free-solid-svg-icons';
import CONFIG from '../config/config';
import NewsRow from './NewsRow';
import { formatPercentage } from './utils';
import Tooltip from './Tooltip';
import { authenticatedFetch, publicFetch } from '../utils/authenticatedFetch';
import { useCategories, useListPerformance, useGuidanceSeason, useHeadlineMarkets } from '../services/api';
import { headlineMarketBadges } from '../utils/headlineMarketBadges';
import { rankListMovers, hasListMovers } from '../utils/listMovers';
import { pickSeason } from '../shared/guidanceSeason';
import { ListMoversSummary, ListMoversDetail } from './ListMoversCard';
import { GuidanceSeasonSummary, GuidanceSeasonDetail } from './GuidanceSeasonCard';
import { renderMarketText } from '../utils/marketText';
import { formatMarketOverviewUpdated } from '../utils/marketOverviewFreshness';
import '../styles/MarketAnalysis.css';
import '../styles/MarketCards.css';
import DisclaimerMessage from './DisclaimerMessage';

import MacroScenariosSection from './MacroScenariosSection';
import BondInsightsSection from './BondInsightsSection';
import FearGreedSection from './FearGreed';
import MarketOverviewSection from './MarketOverviewSection';
import MarketCard from './MarketCard';
import NotificationBell from './NotificationBell';
import MarketHeatmap from './MarketHeatmap';

// A momentum figure is null when NO constituent supplied the underlying data (issue #94) —
// that is "unknown", not "flat", and must not be painted as a gain. Note `null >= 0` is TRUE
// in JavaScript, so a bare `x >= 0 ? positive : negative` silently colours an N/A cell green.
const momentumClass = (value) => value == null
  ? 'momentum-value--muted'
  : value >= 0 ? 'momentum-value--positive' : 'momentum-value--negative';

const momentumText = (value) => value != null ? formatPercentage(value) : '\u2014';

const tierLabels = {
  mega: <span className="market-cap-label">Mega Cap <span className="market-cap-range">(&gt;$200B)</span></span>,
  large: <span className="market-cap-label">Large Cap <span className="market-cap-range">($10B-$200B)</span></span>,
  mid: <span className="market-cap-label">Mid Cap <span className="market-cap-range">($2B-$10B)</span></span>,
  small: <span className="market-cap-label">Small Cap <span className="market-cap-range">($300M-$2B)</span></span>,
  micro: <span className="market-cap-label">Micro Cap <span className="market-cap-range">(&lt;$300M)</span></span>
};

// Add new error boundary component
const ErrorBoundarySection = ({ children, error, isLoading }) => {
  if (isLoading) return <div className="p-3 text-gray-500">Loading...</div>;
  if (error) return (
    <div className="p-3 text-red-500 text-sm">
      <FiAlertTriangle className="inline mr-2" />
      Unable to load data: {error.message || 'Unknown error'}
    </div>
  );
  return children;
};

// Separate Market Pulse section
const MarketPulseSection = ({ data, isLoading, error }) => {
  return (
    <ErrorBoundarySection error={error} isLoading={isLoading}>
      {data && getMarketPulseAnalysis(data)}
    </ErrorBoundarySection>
  );
};

// Move MarketAnalysisSection component definition before its usage
const MarketAnalysisSection = ({ data, isLoading, error }) => {
  if (!data && !error && !isLoading) return null;
  
  return (
    <ErrorBoundarySection error={error} isLoading={isLoading}>
      {data?.analysis && (
        <>
          <div className="py-2.5 text-gray-600">
            {data.analysis.Summary?.marketOutlook || 'No market outlook available'}
          </div>
          
          {/* Add Sector Outlook */}
          {data.analysis.Summary?.sectorOutlook && (
            <div className="mt-3 sector-outlook-container">
              <h4 className="text-sm font-semibold">Sector Outlook:</h4>
              <div className="sector-outlook">
                {Object.entries(data.analysis.Summary.sectorOutlook).map(([sector, sectorData]) => (
                  <Tooltip 
                    key={sector} 
                    text={
                      <div className="max-w-xs">
                        <div className="font-semibold mb-1">{sectorData.analysis}</div>
                        <div className="mt-2">
                          <span className="font-medium">Key Drivers:</span>
                          <ul className="list-disc pl-4 mt-1">
                            {sectorData.keyDrivers.map((driver, idx) => (
                              <li key={idx}>{driver}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    }
                  >
                    <div className={`sector-badge ${sectorData.sentiment}`}>
                      <span className="font-medium capitalize">{sector}</span>
                      <span className="mx-1">•</span>
                      <span className="capitalize">{sectorData.sentiment}</span>
                    </div>
                  </Tooltip>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </ErrorBoundarySection>
  );
};

// Move getMarketPulseAnalysis outside of MarketAnalysisRow component
const getMarketPulseAnalysis = (rawData) => {
  if (!rawData?.data) return null;
  
  const {
    numPriceIncreases,
    numPriceDecreases,
    topIndustriesIncreases,
    topIndustriesDecreases,
    marketCapTiers
  } = rawData.data;
  
  return (
    <div className="space-y-4">
      {/* Market Cap Tiers Table */}
      {marketCapTiers && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-2 text-left font-medium text-gray-700">Market Cap</th>
                <th className="px-3 py-2 text-right font-medium text-green-700">Up %</th>
                <th className="px-3 py-2 text-right font-medium text-red-700">Down %</th>
                <th className="px-3 py-2 text-right font-medium text-green-700">Avg Up</th>
                <th className="px-3 py-2 text-right font-medium text-red-700">Avg Down</th>
                <th className="px-3 py-2 text-right font-medium text-blue-700">Near High</th>
                <th className="px-3 py-2 text-right font-medium text-orange-700">Near Low</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {marketCapTiers.map((tier) => (
                <tr key={tier.market_cap_tier} className="hover:bg-gray-50">
                  <td className="px-3 py-2">{tierLabels[tier.market_cap_tier]}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={tier.increasing_percentage >= 50 ? 'text-green-600' : 'text-gray-600'}>
                      {formatPercentage(tier.increasing_percentage)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className="text-red-600">
                      {formatPercentage(100 - tier.increasing_percentage)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-green-600">
                    {formatPercentage(tier.avg_increase)}
                  </td>
                  <td className="px-3 py-2 text-right text-red-600">
                    {formatPercentage(tier.avg_decrease)}
                  </td>
                  <td className="px-3 py-2 text-right text-blue-600">
                    {formatPercentage(tier.near_high_percentage)}
                  </td>
                  <td className="px-3 py-2 text-right text-orange-600">
                    {formatPercentage(tier.near_low_percentage)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="market-sectors">
        <div className="sector-group">
          <span className="sector-label sector-label-leading">Leading:</span>
          <span className="sector-list">
            {topIndustriesIncreases
              ?.slice(0, 3)
              .map(item => `${item.industry} (${Math.round(item.percentage)}%)`)
              .join(', ')}
          </span>
        </div>
        <div className="sector-group">
          <span className="sector-label sector-label-lagging">Lagging:</span>
          <span className="sector-list">
            {topIndustriesDecreases
              ?.slice(0, 3)
              .map(item => `${item.industry} (${Math.round(item.percentage)}%)`)
              .join(', ')}
          </span>
        </div>
      </div>
    </div>
  );
};


const phaseDetails = {
  EARLY_RECOVERY: {
    sectors: ['Financials', 'Consumer Discretionary', 'Industrials'],
    description: 'Leadership from rate-sensitive and cyclical sectors'
  },
  EXPANSION: {
    sectors: ['Technology', 'Materials', 'Energy'],
    description: 'Growth and commodity sectors lead'
  },
  LATE_CYCLE: {
    sectors: ['Energy', 'Materials', 'Healthcare'],
    description: 'Defensive growth and inflation beneficiaries'
  },
  CONTRACTION: {
    sectors: ['Utilities', 'Consumer Staples', 'Healthcare'],
    description: 'Defensive sectors outperform'
  }
};

const MarketPhaseVisual = ({ currentPhase, confidence }) => {
  const phases = ['EARLY_RECOVERY', 'EXPANSION', 'LATE_CYCLE', 'CONTRACTION'];
  
  return (
    <div className="market-cycle-indicator">
      {phases.map((phase, index) => (
        <div key={phase} className={`cycle-phase ${phase === currentPhase ? 'active-phase' : ''}`}>
          <div className="phase-header">
            <div className="phase-number">{index + 1}</div>
            <div className="phase-name">
              {phase.replace('_', ' ')}
            </div>
            {phase === currentPhase && (
              <div className="confidence-badge">{confidence} Confidence</div>
            )}
          </div>
          <div className="phase-details">
            <div className="typical-sectors">
              {phaseDetails[phase].sectors.join(' • ')}
            </div>
            <div className="phase-description text-xs text-gray-600">
              {phaseDetails[phase].description}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const CapTierCell = ({ large, mid, small }) => (
  <div className="cap-tier-cell">
    <span className={`momentum-value ${momentumClass(large)}`}>
      {formatPercentage(large)}
    </span>
    <span className="cap-tier-divider">/</span>
    <span className={`momentum-value ${momentumClass(mid)}`}>
      {formatPercentage(mid)}
    </span>
    <span className="cap-tier-divider">/</span>
    <span className={`momentum-value ${momentumClass(small)}`}>
      {formatPercentage(small)}
    </span>
  </div>
);

const RangePositionCell = ({ range }) => {
  if (!range) return <span className="text-gray-400">—</span>;
  const getColor = (pct) => {
    if (pct == null) return '#9ca3af';
    if (pct >= 70) return '#059669';
    if (pct >= 40) return '#d97706';
    return '#dc2626';
  };
  return (
    <div className="cap-tier-cell">
      {['large', 'mid', 'small'].map((tier, i) => (
        <React.Fragment key={tier}>
          {i > 0 && <span className="cap-tier-divider">/</span>}
          <span style={{ color: getColor(range[tier]), fontWeight: 500 }}>
            {range[tier] != null ? `${Math.round(range[tier])}%` : '—'}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

const rotationPhaseLabels = {
  EARLY: 'Early',
  BROADENING: 'Broadening',
  MATURE: 'Mature',
  EXHAUSTING: 'Exhausting',
  ROTATING_OUT: 'Rotating Out'
};

const SectorRowGroup = ({ sector, hasCap, colCount }) => {
  const [expanded, setExpanded] = useState(false);
  const industries = sector.industries || [];
  const hasIndustries = industries.length > 0;

  return (
    <>
      <tr
        className={`sector-row ${hasIndustries ? 'sector-row--expandable' : ''} ${expanded ? 'sector-row--expanded' : ''}`}
        onClick={() => hasIndustries && setExpanded(!expanded)}
      >
        <td>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {hasIndustries && (
              <span className="sector-expand-icon">{expanded ? <FiChevronDown /> : <FiChevronRight />}</span>
            )}
            {sector.sector}
          </span>
        </td>
        <td>
          {hasCap && sector.cap_breakdown ? (
            <CapTierCell large={sector.cap_breakdown.large} mid={sector.cap_breakdown.mid} small={sector.cap_breakdown.small} />
          ) : (
            <span className={`momentum-value ${momentumClass(sector.daily_momentum)}`}>
              {formatPercentage(sector.daily_momentum)}
            </span>
          )}
        </td>
        {hasCap && (
          <td>
            <RangePositionCell range={sector.cap_breakdown?.range} />
          </td>
        )}
        <td className={`momentum-value ${momentumClass(sector.weekly_momentum)}`}>
          {momentumText(sector.weekly_momentum)}
        </td>
        <td className={`momentum-value ${momentumClass(sector.monthly_momentum)}`}>
          {momentumText(sector.monthly_momentum)}
        </td>
        <td className={`momentum-value ${momentumClass(sector.quarterly_momentum)}`}>
          {momentumText(sector.quarterly_momentum)}
        </td>
        <td>
          {sector.rotation_phase ? (
            <span className={`rotation-phase-badge rotation-phase-badge--${sector.rotation_phase.toLowerCase().replace('_', '-')}`}>
              {rotationPhaseLabels[sector.rotation_phase] || sector.rotation_phase}
            </span>
          ) : (
            <span className={`momentum-state-badge momentum-state-badge--${sector.momentum_state?.toLowerCase().replace('_', '-')}`}>
              {sector.momentum_state?.replace('_', ' ')}
            </span>
          )}
        </td>
      </tr>
      {expanded && industries.map((ind) => (
        <tr key={ind.industry} className="industry-row">
          <td style={{ paddingLeft: 28 }}>
            <span className="industry-name">{ind.industry}</span>
            <span className="industry-count">({ind.count})</span>
          </td>
          <td>
            {hasCap && ind.cap_breakdown ? (
              <CapTierCell large={ind.cap_breakdown.large} mid={ind.cap_breakdown.mid} small={ind.cap_breakdown.small} />
            ) : (
              <span className={`momentum-value ${momentumClass(ind.daily_momentum)}`}>
                {formatPercentage(ind.daily_momentum)}
              </span>
            )}
          </td>
          {hasCap && (
            <td>
              <RangePositionCell range={ind.cap_breakdown?.range} />
            </td>
          )}
          <td className={`momentum-value ${momentumClass(ind.weekly_momentum)}`}>
            {momentumText(ind.weekly_momentum)}
          </td>
          <td className={`momentum-value ${momentumClass(ind.monthly_momentum)}`}>
            {momentumText(ind.monthly_momentum)}
          </td>
          <td className={`momentum-value ${momentumClass(ind.quarterly_momentum)}`}>
            {momentumText(ind.quarterly_momentum)}
          </td>
          <td />
        </tr>
      ))}
    </>
  );
};

const RotationSignalCard = ({ signal, type }) => (
  <div className={`rotation-signal rotation-signal--${type}`}>
    <div className="rotation-signal__header">
      <span className="rotation-signal__sector">{signal.sector}</span>
      <span className={`rotation-signal__confidence rotation-signal__confidence--${signal.confidence?.toLowerCase()}`}>
        {signal.confidence}
      </span>
      {signal.timeframe && <span className="rotation-signal__timeframe">{signal.timeframe}</span>}
    </div>
    <div className="rotation-signal__detail">{signal.signal}</div>
  </div>
);

const BusinessModelCategoryRow = ({ category, formatModelName }) => {
  const [expanded, setExpanded] = useState(false);
  const hasSubcategories = category.subcategories?.length > 0;

  return (
    <>
      <tr
        className={`biz-model-row ${hasSubcategories ? 'biz-model-row--expandable' : ''} ${expanded ? 'biz-model-row--expanded' : ''}`}
        onClick={() => hasSubcategories && setExpanded(!expanded)}
      >
        <td>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {hasSubcategories && (
              <span className="sector-expand-icon">{expanded ? <FiChevronDown /> : <FiChevronRight />}</span>
            )}
            <span className="business-model-name">{formatModelName(category.category)}</span>
          </span>
        </td>
        <td className={`momentum-value ${momentumClass(category.daily_momentum)}`}>
          {formatPercentage(category.daily_momentum)}
        </td>
        <td className={`momentum-value ${momentumClass(category.weekly_momentum)}`}>
          {momentumText(category.weekly_momentum)}
        </td>
        <td className={`momentum-value ${momentumClass(category.monthly_momentum)}`}>
          {momentumText(category.monthly_momentum)}
        </td>
        <td className={`momentum-value ${momentumClass(category.quarterly_momentum)}`}>
          {momentumText(category.quarterly_momentum)}
        </td>
        <td className="text-gray-500">{category.company_count}</td>
        <td>
          <span className={`momentum-state-badge momentum-state-badge--${category.momentum_state?.toLowerCase().replace('_', '-')}`}>
            {category.momentum_state?.replace('_', ' ')}
          </span>
        </td>
      </tr>
      {expanded && category.subcategories?.map((sub) => (
        <tr key={sub.full_model} className="biz-model-subrow">
          <td style={{ paddingLeft: 28 }}>
            <span className="biz-model-subname">{formatModelName(sub.subcategory)}</span>
          </td>
          <td className={`momentum-value ${momentumClass(sub.daily_momentum)}`}>
            {formatPercentage(sub.daily_momentum)}
          </td>
          <td className={`momentum-value ${momentumClass(sub.weekly_momentum)}`}>
            {momentumText(sub.weekly_momentum)}
          </td>
          <td className={`momentum-value ${momentumClass(sub.monthly_momentum)}`}>
            {momentumText(sub.monthly_momentum)}
          </td>
          <td className={`momentum-value ${momentumClass(sub.quarterly_momentum)}`}>
            {momentumText(sub.quarterly_momentum)}
          </td>
          <td className="text-gray-500">{sub.company_count}</td>
          <td>
            <span className={`momentum-state-badge momentum-state-badge--${sub.momentum_state?.toLowerCase().replace('_', '-')}`}>
              {sub.momentum_state?.replace('_', ' ')}
            </span>
          </td>
        </tr>
      ))}
    </>
  );
};

const BusinessModelRotationSection = ({ businessModelCategories, businessModelRotation }) => {
  // Format business model name for display
  const formatModelName = (name) => {
    if (!name) return '';
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Use hierarchical categories if available, fall back to old format
  const categories = businessModelCategories || [];

  if (!categories.length) return null;

  // Get leading and lagging from rotation analysis
  const leadingModels = businessModelRotation?.leading || [];
  const laggingModels = [
    ...(businessModelRotation?.weakening || []),
    ...(businessModelRotation?.lagging || [])
  ];

  return (
    <div className="business-model-rotation">
      <h4 className="business-model-rotation__title">
        <FiBarChart2 style={{ marginRight: 6 }} />
        Business Model Rotation
      </h4>

      {/* Business Model Table with Expandable Categories */}
      <div className="business-model-table-container">
        <table className="market-sectors-table business-model-table">
          <thead>
            <tr>
              <th>Business Model</th>
              <th>Daily</th>
              <th>Weekly</th>
              <th>Monthly</th>
              <th>Quarterly</th>
              <th>Companies</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <BusinessModelCategoryRow
                key={cat.category}
                category={cat}
                formatModelName={formatModelName}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Leading/Lagging Summary */}
      {(leadingModels.length > 0 || laggingModels.length > 0) && (
        <div className="business-model-interpretation">
          <div className="business-model-signals">
            {leadingModels.length > 0 && (
              <div className="business-model-signal-group business-model-signal-group--leading">
                <h5><FiTrendingUp /> Leading</h5>
                {leadingModels.slice(0, 3).map((item, i) => (
                  <div key={i} className="business-model-signal">
                    <span className="business-model-signal__name">{formatModelName(item.category)}</span>
                    <span className="business-model-signal__detail">
                      {formatPercentage(item.daily_momentum)} • {item.company_count} cos
                    </span>
                  </div>
                ))}
              </div>
            )}
            {laggingModels.length > 0 && (
              <div className="business-model-signal-group business-model-signal-group--lagging">
                <h5><FiTrendingDown /> Lagging</h5>
                {laggingModels.slice(0, 3).map((item, i) => (
                  <div key={i} className="business-model-signal">
                    <span className="business-model-signal__name">{formatModelName(item.category)}</span>
                    <span className="business-model-signal__detail">
                      {formatPercentage(item.daily_momentum)} • {item.company_count} cos
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const MarketSummarySection = ({ interpretation }) => {
  const signals = interpretation.rotation_signals;
  const hasSignals = signals && (signals.accumulating?.length > 0 || signals.distributing?.length > 0);

  return (
    <div className="market-summary">
      <div className="summary-card">
        <p className="summary-text">{interpretation.summary}</p>

        {hasSignals && (
          <div className="rotation-signals-container">
            {signals.accumulating?.length > 0 && (
              <div className="rotation-signals-group">
                <h4 className="rotation-signals-title rotation-signals-title--accumulating">
                  <FiTrendingUp style={{ marginRight: 4 }} /> Capital Flowing In
                </h4>
                {signals.accumulating.map((s, i) => (
                  <RotationSignalCard key={i} signal={s} type="accumulating" />
                ))}
              </div>
            )}
            {signals.distributing?.length > 0 && (
              <div className="rotation-signals-group">
                <h4 className="rotation-signals-title rotation-signals-title--distributing">
                  <FiTrendingDown style={{ marginRight: 4 }} /> Capital Flowing Out
                </h4>
                {signals.distributing.map((s, i) => (
                  <RotationSignalCard key={i} signal={s} type="distributing" />
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

const getSentimentDescription = (rating) => {
  const descriptions = {
    'extreme fear': 'Indicates extremely pessimistic market sentiment, historically a contrarian buy signal',
    'fear': 'Shows elevated market pessimism and risk aversion',
    'neutral': 'Balanced market sentiment without strong bias',
    'greed': 'Indicates elevated market optimism and risk appetite',
    'extreme greed': 'Shows extremely optimistic sentiment, historically a contrarian sell signal'
  };
  return descriptions[rating?.toLowerCase()] || '';
};

const MarketAnalysisRow = ({
  analysis,
  guidance,
  relatedEquities,
  onAddSymbol,
  userSettings,
  onSettingsChange,
  news,
  loggedInUser,
  onSelectList
}) => {
  // Track which card is expanded
  const [expandedCard, setExpandedCard] = useState(null);

  // Today's leading / lagging smart lists (issue #341). Both hooks are shared react-query
  // keys already resolved for the list picker, so this costs no extra request; the ranking
  // itself is the one the mobile Brief uses (src/utils/listMovers.js).
  const { data: categories } = useCategories();
  const { data: listPerformance } = useListPerformance();
  const listMovers = useMemo(() => rankListMovers(listPerformance, categories), [listPerformance, categories]);
  // Guidance by earnings season (issue #387): same query key and the same pickSeason as the
  // mobile Brief card, so the two surfaces show one season and one set of counts.
  const { data: guidanceSeasons } = useGuidanceSeason();
  const guidanceSeason = useMemo(() => pickSeason(guidanceSeasons?.seasons), [guidanceSeasons]);
  // Headline markets in the header (issue #413); a symbol with no quote is simply left out.
  const { data: headlineMarkets } = useHeadlineMarkets();
  const marketBadges = useMemo(() => headlineMarketBadges(headlineMarkets), [headlineMarkets]);
  // Track collapsed/expanded state of entire section - collapsed by default
  const [isCollapsed, setIsCollapsed] = useState(true);
  // Heatmap visibility
  const [showHeatmap, setShowHeatmap] = useState(false);
  // News rotation for card summary
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  // Ref for dwell time tracking
  const sectionRef = useRef(null);

  const isLoggedIn = !!loggedInUser?.email;
  const userEmail = loggedInUser?.email;

  // Rotate news in card summary (only when news card is not expanded)
  useEffect(() => {
    if (!news?.length || expandedCard === 'news') return;
    const timer = setInterval(() => {
      setCurrentNewsIndex((prev) => (prev + 1) % news.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [news?.length, expandedCard]);

  // Track dwell time on Market Analysis section
  useEffect(() => {
    const el = sectionRef.current;
    if (el) {
      journeyTracker.observeSection(el, 'Market Analysis');
    }
    return () => {
      if (el) journeyTracker.unobserveSection(el);
    };
  }, []);

  // Fear & Greed query
  const { data: fearAndGreedData, isLoading: isLoadingFearGreed } = useQuery({
    queryKey: ['fearAndGreed'],
    queryFn: async () => {
      const response = await publicFetch('https://api.1stprinciples.pro/fear-greed?full=true');
      if (!response.ok) throw new Error('Failed to fetch fear and greed data');
      return response.json();
    },
    staleTime: 15 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000
  });

  // Daily movement analytics
  const { data: marketStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['dailyMovementAnalytics'],
    queryFn: async () => {
      const response = await publicFetch(CONFIG.API_URLS.DAILY_MOVEMENT_ANALYTICS);
      if (!response.ok) throw new Error('Failed to fetch daily movement analytics');
      return response.json();
    },
    staleTime: 15 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000
  });

  // Sector rotation data
  const { data: sectorRotationData, isLoading: isLoadingSectors, error: sectorsError } = useQuery({
    queryKey: ['sectorRotation'],
    queryFn: async () => {
      const response = await publicFetch(CONFIG.API_URLS.SECTOR_ROTATION);
      if (!response.ok) throw new Error('Failed to fetch sector rotation data');
      return response.json();
    },
    staleTime: 15 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000
  });

  // Macro scenarios
  const {
    data: macroScenariosData,
    isLoading: isLoadingScenarios,
    error: scenariosError
  } = useQuery({
    queryKey: ['macroScenarios'],
    queryFn: async () => {
      const response = await publicFetch(CONFIG.API_URLS.MACRO_SCENARIOS);
      if (!response.ok) throw new Error('Failed to fetch macro scenarios');
      return response.json();
    },
    staleTime: 12 * 60 * 60 * 1000, // 12 hours max
    refetchInterval: 12 * 60 * 60 * 1000,
  });

  // Curves data for bonds summary
  const { data: curvesData } = useQuery({
    queryKey: ['curvesData'],
    queryFn: async () => {
      const resp = await publicFetch(CONFIG.API_URLS.CURVES_DATA);
      if (!resp.ok) throw new Error('Failed to fetch curves data');
      return resp.json();
    },
    staleTime: 15 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000,
  });

  // Market overview for closing summary
  // Refreshes every 15 min to pick up new data generated at 9:45 AM, 1 PM, 4:15 PM ET
  const { data: marketOverviewData } = useQuery({
    queryKey: ['marketOverview'],
    queryFn: async () => {
      const resp = await publicFetch(CONFIG.API_URLS.MARKET_OVERVIEW);
      if (!resp.ok) throw new Error('Failed to fetch market overview');
      return resp.json();
    },
    staleTime: 15 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const closingSummary = marketOverviewData?.overview?.closing_summary;

  // Generation time of the current overview, formatted in ET (e.g. "4:44 PM ET · Wed").
  // Absent when the deployed reader predates the `generated_at` field, in which case
  // nothing extra is rendered rather than guessing from the raw `timestamp` column.
  const closingSummaryTime = useMemo(
    () => formatMarketOverviewUpdated(marketOverviewData?.generated_at),
    [marketOverviewData?.generated_at]
  );
  const isOverviewStale = marketOverviewData?.stale === true;

  // Get top scenarios for summary
  const topScenarios = useMemo(() => {
    if (!macroScenariosData?.scenarios) return [];
    return [...macroScenariosData.scenarios]
      .sort((a, b) => b.probability - a.probability)
      .slice(0, 3);
  }, [macroScenariosData]);

  // Get leading/lagging sectors with momentum % - fall back to improving/weakening if no strong signals
  const leadingSectors = useMemo(() => {
    const analysis = sectorRotationData?.sectorAnalysis;
    if (analysis?.leading?.length > 0) return analysis.leading.slice(0, 2);
    if (analysis?.improving?.length > 0) return analysis.improving.slice(0, 2);
    // Ultimate fallback: top sectors by daily momentum from sectorDetails
    const details = sectorRotationData?.sectorDetails || [];
    return details.filter(s => s.daily_momentum > 0).slice(0, 2);
  }, [sectorRotationData]);

  const laggingSectors = useMemo(() => {
    const analysis = sectorRotationData?.sectorAnalysis;
    if (analysis?.lagging?.length > 0) return analysis.lagging.slice(0, 2);
    if (analysis?.weakening?.length > 0) return analysis.weakening.slice(0, 2);
    // Ultimate fallback: bottom sectors by daily momentum from sectorDetails
    const details = sectorRotationData?.sectorDetails || [];
    return details.filter(s => s.daily_momentum < 0).slice(-2).reverse();
  }, [sectorRotationData]);

  // Get top leading/lagging business models for header summary
  const topBizModels = useMemo(() => {
    const categories = sectorRotationData?.businessModelCategories || [];
    if (!categories.length) return { leading: [], lagging: [] };
    const sorted = [...categories].sort((a, b) => b.daily_momentum - a.daily_momentum);
    return {
      leading: sorted.filter(c => c.daily_momentum > 0).slice(0, 2),
      lagging: sorted.filter(c => c.daily_momentum < 0).slice(-2).reverse()
    };
  }, [sectorRotationData]);

  // Market pulse metrics
  const numIncreasing = marketStats?.data?.numPriceIncreases || 0;
  const numDecreasing = marketStats?.data?.numPriceDecreases || 0;
  const total = numIncreasing + numDecreasing;
  const increasingPercent = total ? Math.round((numIncreasing / total) * 100) : 0;
  const decreasingPercent = total ? Math.round((numDecreasing / total) * 100) : 0;
  const fearGreedScore = fearAndGreedData?.current?.score ?? 0;
  const fearGreedRating = fearAndGreedData?.current?.rating?.toLowerCase() ?? 'neutral';
  const currentPhase = sectorRotationData?.marketCycle?.currentPhase;

  // Bond yields for summary
  const bondYields = useMemo(() => {
    if (!curvesData?.yield_curve) return { short: null, long: null, slope: null };
    const short = curvesData.yield_curve['3M'];
    const long = curvesData.yield_curve['10Y'];
    const slope = short != null && long != null ? (long - short).toFixed(2) : null;
    return { short, long, slope };
  }, [curvesData]);

  const handleCardToggle = (cardId) => {
    const isExpanding = expandedCard !== cardId;
    setExpandedCard(isExpanding ? cardId : null);
    if (isExpanding) {
      journeyTracker.trackAction('market_card_expand', { card: cardId });
    }
  };

  // Render card summaries with actual data

  const renderPulseSummary = () => (
    <div>
      <div className="market-card__summary-row">
        <span className={`market-card__summary-value ${increasingPercent >= 50 ? 'market-card__summary-value--positive' : 'market-card__summary-value--negative'}`}>
          {increasingPercent}% advancing
        </span>
        <span style={{ margin: '0 6px', color: '#9ca3af' }}>•</span>
        <span style={{ color: '#6b7280' }}>{currentPhase?.replace('_', ' ')} phase</span>
      </div>
      {/* Business model highlights */}
      {(topBizModels.leading.length > 0 || topBizModels.lagging.length > 0) && (
        <div className="sectors-summary__items" style={{ marginTop: 4 }}>
          {topBizModels.leading.length > 0 && (
            <span className="sectors-summary__item sectors-summary__item--up">
              <FiTrendingUp />
              {topBizModels.leading.map(m => `${m.category} ${formatPercentage(m.daily_momentum)}`).join(', ')}
            </span>
          )}
          {topBizModels.lagging.length > 0 && (
            <span className="sectors-summary__item sectors-summary__item--down">
              <FiTrendingDown />
              {topBizModels.lagging.map(m => `${m.category} ${formatPercentage(m.daily_momentum)}`).join(', ')}
            </span>
          )}
        </div>
      )}
    </div>
  );

  const renderFearGreedSummary = () => (
    <div>
      <div className="market-card__summary-row">
        <span className={`market-card__summary-value ${
          fearGreedRating?.includes('greed') ? 'market-card__summary-value--positive' :
          fearGreedRating?.includes('fear') ? 'market-card__summary-value--negative' : ''
        }`} style={{ fontSize: '1.25rem', fontWeight: 600 }}>
          {fearGreedScore.toFixed(0)}
        </span>
        <span style={{ marginLeft: 8, color: '#6b7280', textTransform: 'capitalize' }}>
          {fearGreedRating}
        </span>
      </div>
    </div>
  );

  const renderOutlookSummary = () => (
    <div>
      {topScenarios.map((scenario, idx) => (
        <span
          key={idx}
          className={`market-card__scenario-pill ${
            scenario.probability >= 40 ? 'market-card__scenario-pill--high' :
            scenario.probability >= 25 ? 'market-card__scenario-pill--medium' :
            'market-card__scenario-pill--low'
          }`}
        >
          {scenario.title} <span style={{ opacity: 0.7 }}>{Math.round(scenario.probability)}%</span>
        </span>
      ))}
    </div>
  );

  const renderSectorsSummary = () => (
    <div className="sectors-summary">
      <div className="sectors-summary__items">
        {leadingSectors.length > 0 && (
          <span className="sectors-summary__item sectors-summary__item--up">
            <FiTrendingUp />
            {leadingSectors.map(s => `${s.sector} ${formatPercentage(s.daily_momentum)}`).join(', ')}
          </span>
        )}
        {laggingSectors.length > 0 && (
          <span className="sectors-summary__item sectors-summary__item--down">
            <FiTrendingDown />
            {laggingSectors.map(s => `${s.sector} ${formatPercentage(s.daily_momentum)}`).join(', ')}
          </span>
        )}
      </div>
    </div>
  );

  const renderBondsSummary = () => (
    <div>
      <div className="market-card__summary-row">
        <span className="market-card__summary-label">3M:</span>
        <span className="market-card__summary-value">{bondYields.short?.toFixed(2) ?? 'N/A'}%</span>
        <span style={{ margin: '0 6px', color: '#9ca3af' }}>•</span>
        <span className="market-card__summary-label">10Y:</span>
        <span className="market-card__summary-value">{bondYields.long?.toFixed(2) ?? 'N/A'}%</span>
      </div>
      <div className="market-card__summary-row" style={{ marginTop: 4 }}>
        <span className="market-card__summary-label">Slope:</span>
        <span className={`market-card__summary-value ${
          bondYields.slope < 0 ? 'market-card__summary-value--negative' :
          bondYields.slope > 0.5 ? 'market-card__summary-value--positive' : ''
        }`}>
          {bondYields.slope ?? 'N/A'}%
          {bondYields.slope != null && (
            <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#6b7280' }}>
              {bondYields.slope < 0 ? '(inverted)' : bondYields.slope < 0.5 ? '(flat)' : '(normal)'}
            </span>
          )}
        </span>
      </div>
    </div>
  );

  const renderNewsSummary = () => {
    if (!news || news.length === 0) return <span>No news available</span>;
    const article = news[currentNewsIndex] || news[0];
    return (
      <div className="market-card__news-preview" style={{ transition: 'opacity 0.3s ease' }}>
        {article.imageUrl && (
          <div className="market-card__news-thumbnail">
            <img src={article.imageUrl} alt={article.title || 'News thumbnail'} />
          </div>
        )}
        <span className="market-card__news-title">{article.title}</span>
      </div>
    );
  };

  return (
    <div ref={sectionRef} className={`market-cards-container ${isCollapsed ? 'market-cards-container--collapsed' : ''}`}>
      {/* Header with vitals and toggle */}
      <div className="market-cards-header" onClick={() => setIsCollapsed(!isCollapsed)} style={{ cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="market-card__chevron">
            {isCollapsed ? <FiChevronDown /> : <FiChevronUp />}
          </span>
          <h2 className="market-cards-header__title">Market Analysis</h2>
          <button
            className={`heatmap-toggle-btn ${showHeatmap ? 'heatmap-toggle-btn--active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setShowHeatmap(!showHeatmap); }}
            title="Market Heatmap"
          >
            <FiGrid />
          </button>
          <NotificationBell
            listId="market_overview"
            isLoggedIn={isLoggedIn}
            userEmail={userEmail}
            variant="pill"
            subscribeText="Subscribe to email updates"
            subscribedText="Subscribed"
          />
        </div>

        {/* Vitals - always visible in header */}
        <div className="market-cards-header__metrics" onClick={(e) => e.stopPropagation()}>
          {marketBadges.map((badge) => (
            <Tooltip key={badge.symbol} text={badge.title}>
              <span className={`market-metric market-metric--${badge.tone}`}>{badge.text}</span>
            </Tooltip>
          ))}
          <Tooltip text={`${increasingPercent}% of the ${total.toLocaleString()} stocks we track rose vs their previous close (all stocks, not just your watchlist).`}>
            <span className={`market-metric ${increasingPercent >= 50 ? 'market-metric--positive' : 'market-metric--negative'}`}>
              Stocks up: {increasingPercent}%
            </span>
          </Tooltip>
          <Tooltip text={getSentimentDescription(fearGreedRating)}>
            <span className={`market-metric ${
              fearGreedRating?.includes('greed') ? 'market-metric--positive' :
              fearGreedRating?.includes('fear') ? 'market-metric--negative' : 'market-metric--neutral'
            }`}>
              F&G: {fearGreedScore.toFixed(0)}
            </span>
          </Tooltip>
          {bondYields.slope != null && (
            <span className={`market-metric ${
              bondYields.slope < 0 ? 'market-metric--negative' : 'market-metric--neutral'
            }`}>
              Yield: {bondYields.slope}%
            </span>
          )}
        </div>

        {/* Closing summary - always visible, collapsed or expanded */}
        {closingSummary && (
          <div className="market-cards-header__summary">
            {renderMarketText(closingSummary)}
            {closingSummaryTime && (
              <span
                className={`market-cards-header__summary-time${isOverviewStale ? ' market-cards-header__summary-time--stale' : ''}`}
              >
                {' '}{isOverviewStale ? `Out of date — last updated ${closingSummaryTime}` : `Updated ${closingSummaryTime}`}
              </span>
            )}
          </div>
        )}

      </div>

      {/* Heatmap Panel */}
      {showHeatmap && (
        <MarketHeatmap
          sectorDetails={sectorRotationData?.sectorDetails}
          businessModelCategories={sectorRotationData?.businessModelCategories}
        />
      )}

      {/* Cards Grid - 6 cards */}
      <div className="market-cards-grid">
        {/* Market Pulse Card */}
        <MarketCard
          title="Market Pulse"
          icon={<FiActivity />}
          summary={renderPulseSummary()}
          isExpanded={expandedCard === 'pulse'}
          onToggle={() => handleCardToggle('pulse')}
          isLoading={isLoadingStats}
        >
          <MarketPulseSection data={marketStats} isLoading={false} />
          {currentPhase && (
            <div style={{ marginTop: 16 }}>
              <MarketPhaseVisual currentPhase={currentPhase} confidence={sectorRotationData?.marketCycle?.confidence} />
            </div>
          )}

          {/* Business Model Rotation */}
          <BusinessModelRotationSection
            businessModelCategories={sectorRotationData?.businessModelCategories}
            businessModelRotation={sectorRotationData?.businessModelAnalysis}
          />
        </MarketCard>

        {/* Fear & Greed Card */}
        <MarketCard
          title="Fear & Greed"
          icon={<FiTarget />}
          summary={renderFearGreedSummary()}
          isExpanded={expandedCard === 'feargreed'}
          onToggle={() => handleCardToggle('feargreed')}
          isLoading={isLoadingFearGreed}
        >
          <FearGreedSection fearAndGreedData={fearAndGreedData} />
        </MarketCard>

        {/* Outlook Card */}
        <MarketCard
          title="Outlook"
          icon={<FiBarChart2 />}
          summary={renderOutlookSummary()}
          isExpanded={expandedCard === 'outlook'}
          onToggle={() => handleCardToggle('outlook')}
          isLoading={isLoadingScenarios}
          error={scenariosError}
        >
          <MacroScenariosSection
            scenarios={macroScenariosData?.scenarios || []}
            isLoading={false}
            error={null}
            lastUpdated={macroScenariosData?.timestamp}
            embedded={true}
          />
        </MarketCard>

        {/* Sectors Card */}
        <MarketCard
          title="Sectors"
          icon={<FiTrendingUp />}
          summary={renderSectorsSummary()}
          isExpanded={expandedCard === 'sectors'}
          onToggle={() => handleCardToggle('sectors')}
          isLoading={isLoadingSectors}
          error={sectorsError}
        >
          <div className="sector-performance">
            {(() => {
              const hasCap = !!sectorRotationData?.sectorDetails?.[0]?.cap_breakdown;
              return (
                <table className="market-sectors-table">
                  <thead>
                    <tr>
                      <th>Sector</th>
                      <th>
                        <Tooltip text="Large Cap (≥$10B) / Mid Cap ($2-10B) / Small Cap (<$2B)">
                          <span>{hasCap ? 'Daily (L / M / S)' : 'Daily'}</span>
                        </Tooltip>
                      </th>
                      {hasCap && (
                        <th>
                          <Tooltip text="Where each cap tier sits in its 52-week price range (0% = at low, 100% = at high)">
                            <span>52W Range (L / M / S)</span>
                          </Tooltip>
                        </th>
                      )}
                      <th>Weekly</th>
                      <th>Monthly</th>
                      <th>Quarterly</th>
                      <th>Phase</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectorRotationData?.sectorDetails?.map((sector) => (
                      <SectorRowGroup key={sector.sector} sector={sector} hasCap={hasCap} />
                    ))}
                  </tbody>
                </table>
              );
            })()}
            {sectorRotationData?.interpretation?.summary && (
              <MarketSummarySection interpretation={sectorRotationData.interpretation} />
            )}
          </div>
        </MarketCard>

        {/* Bonds Card */}
        <MarketCard
          title="Bonds"
          icon={<FiDollarSign />}
          summary={renderBondsSummary()}
          isExpanded={expandedCard === 'bonds'}
          onToggle={() => handleCardToggle('bonds')}
        >
          <BondInsightsSection
            isLoading={false}
            error={null}
            embedded={true}
          />
        </MarketCard>

        {/* News Card */}
        <MarketCard
          title="News"
          icon={<FiFileText />}
          summary={renderNewsSummary()}
          isExpanded={expandedCard === 'news'}
          onToggle={() => handleCardToggle('news')}
        >
          <NewsRow
            onAddSymbol={onAddSymbol}
            userSettings={userSettings}
            onSettingsChange={onSettingsChange}
            news={news}
            embedded={true}
          />
        </MarketCard>

        {/* Guidance by season (issue #387) — omitted entirely when no season has enough calls. Sits
            LEFT of Smart Lists (user's placement), taking the first third of the row. */}
        {guidanceSeason && (
          <MarketCard
            title="Guidance"
            icon={<FiCompass />}
            summary={<GuidanceSeasonSummary season={guidanceSeason} />}
            isExpanded={expandedCard === 'guidance'}
            onToggle={() => handleCardToggle('guidance')}
          >
            <GuidanceSeasonDetail season={guidanceSeason} />
          </MarketCard>
        )}

        {/* Smart Lists Card — omitted entirely when nothing ranks, never an empty card */}
        {hasListMovers(listMovers) && (
          <MarketCard
            title="Smart Lists"
            icon={<FiGrid />}
            // Takes the two thirds to the RIGHT of the Guidance card when there is one (issue
            // #387); full row otherwise — including while Guidance is expanded onto its own row,
            // or a third of this row sits empty.
            className={guidanceSeason && expandedCard !== 'guidance' ? 'market-card--two-thirds' : 'market-card--wide'}
            summary={<ListMoversSummary movers={listMovers} onSelectList={onSelectList} />}
            isExpanded={expandedCard === 'lists'}
            onToggle={() => handleCardToggle('lists')}
          >
            <ListMoversDetail movers={listMovers} onSelectList={onSelectList} />
          </MarketCard>
        )}
      </div>
    </div>
  );
};

export default MarketAnalysisRow;
